import { parseCardId, RANK_ACE, type CardId, type Seat, type Suit } from "@bing/engine-core";
import type { ShelemOutcome, TrickPlay } from "./types";

// ── The canonical numbers (the Persian rules: audit 2026-10-09 §1.2, D1–D18) ─────────────────

/** Points a single card is worth when captured: Ace = 10, Ten = 10, Five = 5, else 0. */
export const CARD_POINT_TABLE: Readonly<Record<number, number>> = { [RANK_ACE]: 10, 10: 10, 5: 5 };

/** Card-points in a full 52-card deck (no jokers, D1): (A 10 + 10 10 + 5 5) × 4 suits. */
export const CARD_POINTS_TOTAL = 100;
/** Every won trick is worth 5 points. */
export const TRICK_POINTS_EACH = 5;
/** 48 cards dealt into hands ⇒ exactly 12 tricks are played per hand. */
export const TRICKS_PER_HAND = 12;
/** The کف counts as one more won trick for the حاکم's team (D2/D8): its card points + these 5. */
export const WIDOW_TRICK_POINTS = TRICK_POINTS_EACH;
/** What every hand distributes: 100 card points + 13 tricks × 5 (the 12 played + the کف). */
export const HAND_POINTS_TOTAL = CARD_POINTS_TOTAL + (TRICKS_PER_HAND + 1) * TRICK_POINTS_EACH; // 165

/** Bids are multiples of 5 from 100 up to all 165 points (D3); the شلم call sits above them. */
export const MIN_BID = 100;
export const MAX_BID = 165;
export const BID_STEP = 5;
/** A called شلم made (+) or failed (−) (D4) — the CALL's number alone: an unannounced sweep scores
 *  the hand's {@link HAND_POINTS_TOTAL} (card-98, the operator 2026-10-27: «a team that takes all
 *  12 tricks without calling «شلم» scores 165, not 330; 330 only for a called and made شلم»). */
export const SHELEM_SCORE = 330;

export const CARDS_PER_HAND = 12;
export const WIDOW_SIZE = 4;
export const SEATS = 4;
/** The engine's FALLBACK target, for a state built with no `targetScore` rule (D13). No room relies
 *  on it since card-67: every شلم room hands its target in explicitly — public 660, a private host's
 *  (the private-mode tournament room's too) or a solo player's pick, default 660 (`@bing/shared`'s
 *  `DEFAULT_SHELEM_TARGET`) — and a saved game carries the target it was dealt under. */
export const DEFAULT_TARGET = 1200;
/** A public quick-match plays to 660 (D13). */
export const PUBLIC_TARGET = 660;
/** The targets a private table's host — and a solo player — may choose between. */
export const TARGET_OPTIONS = [660, 1200] as const;
/** Hard cap on PLAYED hands per match: after this many hands played to the 12th trick the match
 *  ends even if nobody reached the target, by the same higher-total rule. It bounds played hands
 *  ONLY — redeals do not count (D6), so an auction in which every seat always passes redeals for
 *  ever; in practice a human or the turn timer's auto-play ends it. A saved game keeps the cap it
 *  was dealt under (`maxHands` lives in the state).
 *
 *  80 since the 2026-10-11 ruling (was 40). Re-measured 2026-10-21 under card-a7's −bid (a miss
 *  below the other team no longer −2 × bid) — the same scripts and seeds, 5,000 matches per table
 *  and target (docs/audits/pasur-shelem-2026-10-09/evidence/shelem/sim-cap-2026-10-10.md): four
 *  medium or four hard bots never pass 27 hands at 1200 or 17 at 660 (none reached 40); a
 *  solo-shaped «easy» table — a medium bot for the player, three easy bots, at 1200 — played all
 *  40 in 2.28 % of matches and 80 in none (the longest, 64). Four easy bots, a table no room
 *  seats, play 80 in 13.2 % at 1200 and 0.8 % at 660 (48.5 % and 13.5 % under −2 × bid): the easy
 *  tier's own bidding, recorded for the operator — and re-tuned by card-a7 B9 (2026-10-21, the same
 *  seeds, 5,000 a table): four easy bots now never pass 50 hands at 1200 or 29 at 660, the
 *  solo-shaped table 33, so the cap binds at no measured table. */
export const DEFAULT_MAX_HANDS = 80;

// ── Card parsing (memoized — legality/scoring run on the hot path) ─────────

const SUIT_CACHE = new Map<CardId, Suit>();
const RANK_CACHE = new Map<CardId, number>();

export function suitOf(id: CardId): Suit {
  let suit = SUIT_CACHE.get(id);
  if (suit === undefined) {
    suit = parseCardId(id).suit;
    SUIT_CACHE.set(id, suit);
  }
  return suit;
}

export function rankOf(id: CardId): number {
  let rank = RANK_CACHE.get(id);
  if (rank === undefined) {
    rank = parseCardId(id).rank;
    RANK_CACHE.set(id, rank);
  }
  return rank;
}

/** The captured-point value of a single card (0 for non-scoring ranks). */
export function cardPoints(id: CardId): number {
  return CARD_POINT_TABLE[rankOf(id)] ?? 0;
}

/** Sum the card-points across a collection of cards. */
export function pointsInCards(cards: readonly CardId[]): number {
  return cards.reduce((sum, c) => sum + cardPoints(c), 0);
}

/** Partnership of a seat: seats 0 & 2 are team 0, seats 1 & 3 are team 1. */
export function teamOf(seat: Seat): number {
  return seat % 2;
}

// ── Bidding ────────────────────────────────────────────────────────────────

/** Whether `amount` is a legal next bid given the current highest bid (0 = none yet). */
export function isValidBid(amount: number, highBid: number): boolean {
  return (
    Number.isInteger(amount) &&
    amount % BID_STEP === 0 &&
    amount >= MIN_BID &&
    amount <= MAX_BID &&
    amount > highBid
  );
}

/** Every legal bid value given the current highest bid, ascending. */
export function legalBids(highBid: number): number[] {
  const from = Math.max(MIN_BID, highBid + BID_STEP);
  const bids: number[] = [];
  for (let amount = from; amount <= MAX_BID; amount += BID_STEP) bids.push(amount);
  return bids;
}

// ── Trick play ───────────────────────────────────────────────────────────

/**
 * The cards `hand` may legally play into the current trick. Leading: any card (trump may be led
 * at any time — and the حاکم's very first lead NAMES trump). Otherwise: follow the led suit if
 * able, else any card. (شلم has no first-trick or trump-broken restrictions.)
 */
export function legalPlays(hand: readonly CardId[], trick: readonly TrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const ledSuit = suitOf(trick[0]!.card);
  const inSuit = hand.filter((c) => suitOf(c) === ledSuit);
  return inSuit.length > 0 ? inSuit : [...hand];
}

/** Ordering strength of a card within a trick: trumps beat led-suit beat off-suit. */
function cardStrength(card: CardId, ledSuit: Suit, trump: Suit): number {
  const suit = suitOf(card);
  const rank = rankOf(card);
  if (suit === trump) return 200 + rank;
  if (suit === ledSuit) return 100 + rank;
  return rank; // off-suit, non-trump: can never beat the led card
}

/** Winner of a (partial or complete) trick: highest trump, else highest card of the led suit. */
export function trickWinner(trick: readonly TrickPlay[], trump: Suit): Seat {
  const ledSuit = suitOf(trick[0]!.card);
  let best = trick[0]!;
  let bestScore = cardStrength(best.card, ledSuit, trump);
  for (let i = 1; i < trick.length; i++) {
    const play = trick[i]!;
    const score = cardStrength(play.card, ledSuit, trump);
    if (score > bestScore) {
      best = play;
      bestScore = score;
    }
  }
  return best.seat;
}

// ── Point tallying & hand scoring (pure) ───────────────────────────────────

/** A finished hand's points, from the حاکم's team's side. */
export interface HandPoints {
  /** Card points in the tricks the حاکم's team won. */
  readonly declCardPoints: number;
  /** 5 per trick the حاکم's team won. */
  readonly declTrickPoints: number;
  /** The کف: the discards' card points + one trick's 5. */
  readonly widowPoints: number;
  /** The sum of the three above. */
  readonly declPoints: number;
  /** The defenders' card points + 5 per trick. `declPoints + defPoints === 165`. */
  readonly defPoints: number;
}

/**
 * Score the piles of a finished hand. The کف counts as the حاکم's 13th trick (D2/D8): its card
 * points AND 5 trick points go to the حاکم's team, so a hand always distributes
 * {@link HAND_POINTS_TOTAL} (165).
 */
export function handPoints(
  taken: readonly (readonly CardId[])[],
  tricksWon: readonly number[],
  discards: readonly CardId[],
  declaringTeam: number,
): HandPoints {
  let declCardPoints = 0;
  let defCardPoints = 0;
  taken.forEach((cards, seat) => {
    const pts = pointsInCards(cards);
    if (teamOf(seat) === declaringTeam) declCardPoints += pts;
    else defCardPoints += pts;
  });
  const declTrickPoints = tricksWon[declaringTeam]! * TRICK_POINTS_EACH;
  const widowPoints = pointsInCards(discards) + WIDOW_TRICK_POINTS;
  return {
    declCardPoints,
    declTrickPoints,
    widowPoints,
    declPoints: declCardPoints + declTrickPoints + widowPoints,
    defPoints: defCardPoints + tricksWon[1 - declaringTeam]! * TRICK_POINTS_EACH,
  };
}

/**
 * Raw points captured by each team `[t0, t1]` at hand end: card-points in each team's won tricks,
 * plus 5 per trick won, plus (for the حاکم's team only) the کف — its card points and 5 more.
 * Always sums to {@link HAND_POINTS_TOTAL} (165) over a full hand.
 */
export function teamPoints(
  taken: readonly (readonly CardId[])[],
  tricksWon: readonly number[],
  discards: readonly CardId[],
  declaringTeam: number,
): [number, number] {
  const { declPoints, defPoints } = handPoints(taken, tricksWon, discards, declaringTeam);
  return declaringTeam === 0 ? [declPoints, defPoints] : [defPoints, declPoints];
}

export interface HandScore {
  /** Match-score change per team `[t0, t1]`. */
  readonly teamDelta: [number, number];
  readonly made: boolean;
  readonly outcome: ShelemOutcome;
}

/**
 * Score one completed hand into a per-team match-score delta (D4, D10–D12). The defenders keep
 * the points they took in every case except a sweep or a made call.
 *  - **Called شلم**: all 12 tricks → +330, defenders 0; anything less → −330, defenders theirs.
 *  - **Sweep** (all 12 tricks, no call): +165 — the hand's whole {@link HAND_POINTS_TOTAL}, the
 *    points it took — defenders 0 (card-98: 330 until then; 330 is now the call's alone). Only the
 *    حاکم's team can sweep — the defenders taking all 12 tricks is a miss (a یاسا, or a failed call), not a شلم.
 *  - **Made** (`declPoints ≥ bid`): +declPoints (the points TAKEN, not the bid).
 *  - **Missed** (`declPoints < bid`): −bid, and ONLY the bid — the common Iranian rule (card-a7,
 *    2026-10-21: plain −bid in 4 of 8 Persian rule texts; until then −2 × bid below the
 *    defenders). The outcome still names the miss: **یاسا** when `declPoints < defPoints` (80
 *    or less of 165), else **set** — the same −bid.
 *  - **The defenders' twelve** is no case of its own: they score what they took, at most 160 — the
 *    کف is always the حاکم's team's, so they never hold the hand's 165 (card-25, the operator
 *    2026-10-30, undoing card-98's 165 of 2026-10-28). The حاکم's team pays −bid, or −330 for a call.
 */
export function scoreHand(
  declPoints: number,
  defPoints: number,
  bid: number,
  declaringTeam: number,
  sweep: boolean,
  called = false,
): HandScore {
  let outcome: ShelemOutcome;
  let declDelta: number;
  let defDelta = defPoints;
  if (called) {
    outcome = sweep ? "shelemMade" : "shelemFailed";
    declDelta = sweep ? SHELEM_SCORE : -SHELEM_SCORE;
    if (sweep) defDelta = 0;
  } else if (sweep) {
    outcome = "sweep";
    declDelta = HAND_POINTS_TOTAL;
    defDelta = 0;
  } else if (declPoints >= bid) {
    outcome = "made";
    declDelta = declPoints;
  } else {
    outcome = declPoints < defPoints ? "yasa" : "set";
    declDelta = -bid;
  }
  const made = outcome === "made" || outcome === "sweep" || outcome === "shelemMade";
  const teamDelta: [number, number] =
    declaringTeam === 0 ? [declDelta, defDelta] : [defDelta, declDelta];
  return { teamDelta, made, outcome };
}
